import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Container, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { FaArrowLeft } from "react-icons/fa";

const ViewPumpInternationalSalesReturn = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [salesData, setSalesData] = useState(null);
    const [rows, setRows] = useState([]);

    const fetchSalesData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}salesReturn/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setSalesData(data);

            if (data.salesReturnItems && data.salesReturnItems.length > 0) {
                setRows(data.salesReturnItems.map(item => ({
                    description: item.product?.description || '',
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitratecurrency: item.unitratecurrency,
                    totalcurrency: item.totalcurrency,
                    exchangerate: item.exchangerate,
                    unitRateInr: item.unitrateinr,
                    totalInr: item.totalinr,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom,
                    currency: item.currency || "$ (USD)"
                })));
            } else if (data.salesItems && data.salesItems.length > 0) {
                setRows(data.salesItems.map(item => ({
                    description: item.product?.description || '',
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitratecurrency: item.unitratecurrency,
                    totalcurrency: item.totalcurrency,
                    exchangerate: item.exchangerate,
                    unitRateInr: item.unitrateinr,
                    totalInr: item.totalinr,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom,
                    currency: item.currency || "$ (USD)"
                })));
            }
        } catch (error) {
            console.error("Error fetching sales return data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch sales return data. Please try again.",
            });
            navigate("/salesIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0.00';
        return parseFloat(value).toFixed(2);
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.customeraddress,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            case 'Completed': return 'info';
            case 'Open': return 'warning';
            case 'Active': return 'success';
            case 'Returned': return 'warning';
            default: return 'light';
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!salesData) {
        return <div className="alert alert-danger text-center mt-4">Sales Return not found</div>;
    }

    const exportChargesINR = (parseFloat(salesData.exportpreperationchargescurrency || 0) * parseFloat(salesData.salesexchangerate || 1));

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-danger mb-0">PUMP INTERNATIONAL SALES RETURN - VIEW</h5>
                        <div>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-danger ps-2">
                                <small className="text-muted">Return No</small>
                                <div className="fw-bold">{salesData.salesreturnno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Return Date</small>
                                <div className="fw-bold">{formatDate(salesData.salesreturndate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{salesData.salescurrency || '$ (USD)'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(salesData.status)} className="px-2 py-1">
                                        {salesData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Branch & Warehouse */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Branch & Warehouse Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <small className="text-muted">Branch</small>
                            <div className="fw-bold">{salesData.branch?.branchname || '-'}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Warehouse</small>
                            <div className="fw-bold">{salesData.warehouse?.warehousename || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Return Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Return Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Invoice No</small>
                            <div className="fw-bold">{salesData.invoiceno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Invoice Date</small>
                            <div className="fw-bold">{formatDate(salesData.invoicedate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Tally Reference No</small>
                            <div className="fw-bold">{salesData.tallyreferenceno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Dispatch Through</small>
                            <div className="fw-bold">{salesData.dispatchthrough || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Exchange Rate (₹/Currency)</small>
                            <div className="fw-bold">{formatNumber(salesData.salesexchangerate)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Additional Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Additional Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Destination</small>
                            <div className="fw-bold">{salesData.destination || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Reference</small>
                            <div className="fw-bold">{salesData.reference || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Packing List No</small>
                            <div className="fw-bold">{salesData.packinglistno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Packing List Date</small>
                            <div className="fw-bold">{formatDate(salesData.packinglistdate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Buyer Order No</small>
                            <div className="fw-bold">{salesData.buyerorderno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Buyer Order Date</small>
                            <div className="fw-bold">{formatDate(salesData.buyerorderdate)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Customer Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Customer Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Company Name</small>
                            <div className="fw-bold">{salesData.customer?.companyname || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Contact Person</small>
                            <div className="fw-bold">{salesData.contactperson || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">GST No</small>
                            <div className="fw-bold">{salesData.customer?.gstno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Phone</small>
                            <div className="fw-bold">{salesData.contactno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Email</small>
                            <div className="fw-bold">{salesData.email || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Payment Terms</small>
                            <div className="fw-bold">{salesData.customer?.paymentterm || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Incoterms</small>
                            <div className="fw-bold">{salesData.customer?.incoterms || '-'}</div>
                        </Col>
                        <Col md={12}>
                            <small className="text-muted">Address</small>
                            <div className="fw-bold">{getAddressString(salesData.customeraddress)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Bill To & Ship To */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Billing & Shipping Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="border-start border-3 border-primary ps-3">
                                <small className="text-muted">Bill To</small>
                                <div className="fw-bold">{getAddressString(salesData.billto)}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="border-start border-3 border-success ps-3">
                                <small className="text-muted">Ship To</small>
                                <div className="fw-bold">{getAddressString(salesData.shipto)}</div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Sales Return Items */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Sales Return Items</h6>
                </div>
                <div className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (Curr)</th>
                                    <th className="fw-bold py-2 text-end">Total (Curr)</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (₹)</th>
                                    <th className="fw-bold py-2 text-end">Total (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.description || '-'}</div>
                                            {row.productremarks && row.productremarks !== '-' && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td className="text-center">{row.quantity || '0'}</td>
                                        <td className="text-center">{row.uom || '-'}</td>
                                        <td className="text-end">{formatNumber(row.unitratecurrency)}</td>
                                        <td className="text-end">{formatNumber(row.totalcurrency)}</td>
                                        <td className="text-end">{formatCurrency(row.unitRateInr)}</td>
                                        <td className="text-end fw-semibold">{formatCurrency(row.totalInr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Calculation Summary */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <div className="shadow-sm border rounded bg-white">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
                        </div>
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total (INR):</span>
                                <span className="fw-bold">{formatCurrency(salesData.nettotalinr)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total (Currency):</span>
                                <span className="fw-bold">{formatNumber(salesData.nettotalcurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges (Currency):</span>
                                <span className="fw-bold">{formatNumber(salesData.exportpreperationchargescurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges (INR):</span>
                                <span className="fw-bold">{formatCurrency(exportChargesINR)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                <span className="fw-bold">Grand Total (INR)</span>
                                <span className="fw-bold text-primary">{formatCurrency(salesData.grandtotalinr)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <span className="fw-bold">Grand Total (Currency)</span>
                                <span className="fw-bold text-success">{formatNumber(salesData.grandtotalcurrency)}</span>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate("/salesIndexTable/1")}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
};

export default ViewPumpInternationalSalesReturn;